import React, { useState, useMemo, useEffect } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  catchAsync,
  checkResponse,
  removeUnderScoreAndCapitalizeFirstLetter,
} from "../../../../utilities/utilities";
import {
  UPDATE_PROMOCODE,
  ADD_PROMOCODE,
  GET_PROMOCODE_BY_ID,
  HOTELLIST,
} from "../../../../services/ApiCalls";
import { discountTypeArr } from "../../../../utilities/const";
import Loader from "../../../../components/Common/Loader";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { toast } from "react-toastify";

const getSchema = () =>
  z
    .object({
      promocode: z
        .string({ required_error: "Code is required" })
        .min(1, "Code is required"),
      name: z
        .string({ required_error: "Coupon name is required" })
        .min(1, "Coupon name is required"),
      status: z.boolean(),
      type: z.string({ message: "Discount type is required" }),
      endDate: z
        .string({ message: "End date is required" })
        .min(1, "End date is required"),
      startDate: z
        .string({ message: "Start date is required" })
        .min(1, "Start date is required"),
      reusableCount: z
        .string({ required_error: "Reusable Count is required" })
        .refine((val) => !isNaN(val) && Number(val) >= 0, {
          message: "Reusable Count must be a non-negative number",
        }),
      usageLimit: z
        .string({ required_error: "Usage limit is required" })
        .min(1, "Usage limit is required"),
      value: z
        .string()
        .optional()
        .refine(
          (val) =>
            val === undefined ||
            val === "" ||
            (!isNaN(val) && Number(val) >= 0),
          { message: "Discount must be a non-negative number" }
        ),
    })
    .refine(
      (data) => {
        const start = moment(data.startDate);
        const end = moment(data.endDate);
        return start.isBefore(end);
      },
      {
        message: "Start date must be before end date",
        path: ["startDate"],
      }
    );

const AddPromoCode = () => {
  const [promoCodeDetails, setPromocodeDetails] = useState();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;
  const { id } = useParams();
  const schema = useMemo(() => getSchema(id), [id]);
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const { t } = useTranslation();
  const [hotelData, setHotelData] = useState([]);

  const getHotelData = catchAsync(async () => {
    const [hotelRes] = await Promise.all([
      HOTELLIST({ limit: 100, page: 1, status: "Active" }),
    ]);
    checkResponse({ res: hotelRes, setData: setHotelData });
  });

  useEffect(() => {
    getHotelData();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      status: true,
      type: "percent",
    },
    values: {
      promocode: promoCodeDetails?.promocode || "",
      name: promoCodeDetails?.name || "",
      reusableCount: promoCodeDetails?.reusableCount
        ? String(promoCodeDetails.reusableCount)
        : "",
      usageLimit: promoCodeDetails?.usageLimit || "",
      value: promoCodeDetails?.value
        ? String(promoCodeDetails.value)
        : "",      status: promoCodeDetails ? promoCodeDetails.status === "Active" : true,
      type: promoCodeDetails?.type || "percent",
      startDate: promoCodeDetails?.startDate
        ? moment
          .utc(promoCodeDetails?.startDate)
          .local()
          .format("YYYY-MM-DDTHH:mm")
        : "",
      endDate: promoCodeDetails?.endDate
        ? moment
          .utc(promoCodeDetails?.endDate)
          .local()
          .format("YYYY-MM-DDTHH:mm")
        : "",
    },
  });

  const selectedType = watch("type");

  console.log(promoCodeDetails, "promoCodeDetails ");

  const submitHandler = async (data) => {
    try {
      const body = {
        promocode: data.promocode,
        name: data.name,
        type: data.type,
        value: data.value,
        reusableCount: data.reusableCount,
        usageLimit: data.usageLimit,
        status: data.status ? "Active" : "Inactive",
        startDate: moment(data.startDate).utc().format(),
        endDate: moment(data.endDate).utc().format(),
        ...(id && { _id: id }),
      };


      const res = await (id ? UPDATE_PROMOCODE(body) : ADD_PROMOCODE(body));

      if (res?.data?.status === "success") {
        toast.success(res.data.message || "Success");
        navigate(`/promocode?page=${page}`);
      } else {
        toast.error(res?.data?.message || "Something went wrong");
      }
    } catch (error) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error_description ||
        error?.message ||
        "Something went wrong";

      console.error("Full Axios Error:", error);
      toast.error(backendMessage);
    }
  };

  const getData = catchAsync(async () => {
    setLoader(true);
    await GET_PROMOCODE_BY_ID(id).then((res) =>
      checkResponse({
        res,
        setData: setPromocodeDetails,
        setLoader,
      })
    );
  });

  useEffect(() => {
    if (id) getData();
  }, [id]);

  const handleCancel = () => navigate(`/promocode?page=${page}`);

  return (
    <>
      {loader && <Loader />}
      <section className="addPromo position-relative py-3">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to={`/promocode?page=${page}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {!id ? t("coupon.addCoupon") : t("coupon.editCoupon")}
                </h4>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row className="justify-content-between">
                    <Col lg="5" md="6" className="my-2">

                      <div className="py-2">
                        <label>{t("coupon.form.couponName")}</label>
                        <input
                          type="text"
                          placeholder="Coupon Name"
                          className="form-control"
                          {...register("name")}
                        />
                        {errors?.name && (
                          <p className="text-danger">{errors.name.message}</p>
                        )}
                      </div>
                      <Row className="mb-3">
                        <Col md={6}>
                          <label htmlFor="startDate">{t("coupon.form.startDate")}</label>
                          <input
                            type="datetime-local"
                            id="startDate"
                            className="form-control"
                            min={moment().format("YYYY-MM-DDTHH:mm")}
                            defaultValue={
                              promoCodeDetails?.startDate
                                ? moment
                                  .utc(promoCodeDetails.startDate)
                                  .local()
                                  .format("YYYY-MM-DDTHH:mm")
                                : ""
                            }
                            {...register("startDate")}
                          />
                          {errors?.startDate && (
                            <p className="text-danger">
                              {errors.startDate.message}
                            </p>
                          )}
                        </Col>

                        <Col md={6}>
                          <label htmlFor="endDate">{t("coupon.form.endDate")}</label>
                          <input
                            type="datetime-local"
                            id="endDate"
                            className="form-control"
                            min={moment().format("YYYY-MM-DDTHH:mm")}
                            defaultValue={
                              promoCodeDetails?.endDate
                                ? moment
                                  .utc(promoCodeDetails.endDate)
                                  .local()
                                  .format("YYYY-MM-DDTHH:mm")
                                : ""
                            }
                            {...register("endDate")}
                          />
                          {errors?.endDate && (
                            <p className="text-danger">
                              {errors.endDate.message}
                            </p>
                          )}
                        </Col>
                      </Row>

                      <div className="py-2">
                        <label>{t("coupon.form.couponCode")}</label>
                        <input
                          type="text"
                          placeholder="REWARDS4U"
                          className="form-control"
                          {...register("promocode")}
                        />
                        {errors?.promocode && (
                          <p className="text-danger">
                            {errors.promocode.message}
                          </p>
                        )}
                      </div>
                      <div className="py-2">
                        <label>{t("coupon.form.discountType")}</label>
                        <Form.Select
                          className="form-control"
                          {...register("type")}
                        >
                          {discountTypeArr.map((item) => (
                            <option key={item} value={item}>
                              {removeUnderScoreAndCapitalizeFirstLetter(item)}
                            </option>
                          ))}
                        </Form.Select>
                        {errors?.type && (
                          <p className="text-danger">{errors.type.message}</p>
                        )}
                      </div>
                    </Col>

                    <Col lg="5" md="6" className="my-2">
                      {selectedType === "percent" && (
                        <div className="py-2">
                          <label>{t("coupon.form.discount")}</label>
                          <input
                            type="number"
                            placeholder="Discount"
                            className="form-control"
                            {...register("value")}
                            min="0"
                            onKeyDown={(e) => {
                              if (
                                e.key === "-" ||
                                e.key === "e" ||
                                e.key === "E"
                              ) {
                                e.preventDefault();
                              }
                            }}
                          />
                          {errors?.value && (
                            <p className="text-danger">
                              {errors.value.message}
                            </p>
                          )}
                        </div>
                      )}

                      {selectedType === "amount" && (
                        <div className="py-2">
                          <label>{t("coupon.form.couponName")}</label>
                          <input
                            type="number"
                            placeholder="Amount"
                            className="form-control"
                            {...register("value", {
                              min: {
                                value: 0,
                                message: "Amount cannot be negative",
                              },
                            })}
                            onInput={(e) => {
                              if (e.target.value < 0) e.target.value = 0;
                            }}
                            onKeyDown={(e) => {
                              if (
                                e.key === "-" ||
                                e.key === "e" ||
                                e.key === "E"
                              ) {
                                e.preventDefault();
                              }
                            }}
                          />
                          {errors?.value && (
                            <p className="text-danger">
                              {errors.value.message}
                            </p>
                          )}
                        </div>
                      )}

                      <div className="py-2">
                        <label>{t("coupon.form.reusableCount")}</label>
                        <input
                          type="text"
                          placeholder="Reusable Count"
                          className="form-control"
                          {...register("reusableCount", {
                            min: {
                              value: 0,
                              message: "Reusable count cannot be negative",
                            },
                          })}
                          onInput={(e) => {
                            if (e.target.value < 0) e.target.value = 0;
                          }}
                          onKeyDown={(e) => {
                            if (
                              e.key === "-" ||
                              e.key === "e" ||
                              e.key === "E"
                            ) {
                              e.preventDefault();
                            }
                          }}
                        />
                        {errors?.reusableCount && (
                          <p className="text-danger">
                            {errors.reusableCount.message}
                          </p>
                        )}
                      </div>

                      <div className="py-2">
                        <label>{t("coupon.form.usageLimit")}</label>
                        <Form.Select
                          className="form-control"
                          {...register("usageLimit")}
                        >
                          <option value="">Select Usage Limit</option>
                          <option value="Reusable">Reusable</option>
                          <option value="One Time">One Time</option>
                        </Form.Select>
                        {errors?.usageLimit && (
                          <p className="text-danger">
                            {errors.usageLimit.message}
                          </p>
                        )}
                      </div>

                      <div className="py-2">
                        <label>{t("coupon.form.setStatus")}</label>
                        <Form.Check
                          type="switch"
                          id="custom-switch"
                          {...register("status")}
                        />
                      </div>
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-center gap-10">
                        <Button
                          type="button"
                          onClick={handleCancel}
                          className="commonBtn GreyBtn"
                        >
                          {t("coupon.form.cancel")}
                        </Button>
                        <Button type="submit" className="commonBtn">
                          {t("coupon.form.submit")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddPromoCode;
